import { useEffect } from "react";
import { createPortal } from "react-dom";
import { X, PlugZap } from "lucide-react";
import { ManatalIntegrationCard } from "./ManatalIntegrationCard";
import { Button } from '@/components/ui/atoms/Button';

interface IntegrationsSidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

export const IntegrationsSidebar = ({ isOpen, onClose }: IntegrationsSidebarProps) => {
  // Handle escape key to close
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const sidebarContent = (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/30 z-[100] transition-opacity duration-300 ${
          isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Sidebar Panel */}
      <div
        className={`fixed top-0 right-0 h-full w-full sm:w-[520px] bg-background-gray z-[101] shadow-2xl transform transition-transform duration-300 ease-in-out ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
        role="dialog"
        aria-modal="true"
        aria-label="Integrations"
      >
        {/* Primary Header - matches main app header height (py-4) */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-gray-200 bg-white">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-gradient-purple to-gradient-pink flex items-center justify-center">
              <PlugZap className="w-4 h-4 text-white" />
            </div>
            <span className="text-dark-invirtus text-xl font-bold">Integrations</span>
          </div>
          <Button
            type="icon"
            intent="muted"
            size="md"
            onClick={onClose}
            aria-label="Close integrations"
            icon={<X />}
          />
        </div>

        {/* Secondary Header - matches Modern SaaS Header from Dashboard */}
        <div className="bg-white border-b border-gray-200">
          <div className="px-6 py-3">
            <div className="flex flex-row justify-between h-auto sm:h-16 items-center">
              <div>
                <h4>Connect your ATS</h4>
                <p className="text-xs sm:text-sm text-dark-invirtus/80 font-body">
                  Sync candidates and automate interview workflows.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto h-[calc(100%-130px)]">
          <ManatalIntegrationCard />
        </div>
      </div>
    </>
  );

  // Use portal to render at document body level to escape overflow:hidden containers
  return createPortal(sidebarContent, document.body);
};
